// src/components/item-builder/MatchingModal.jsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import { Plus, Trash2 } from "lucide-react";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

// فرم خالی برای حالت ایجاد
const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  pairs: [
    { left: { fa: "", en: "" }, right: { fa: "", en: "" } },
    { left: { fa: "", en: "" }, right: { fa: "", en: "" } },
  ],
  layout: "left-right",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

// تبدیل آیتم بک‌اند → فرم برای ادیت
const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";

  const body = item.body || {};
  const rawPairs = Array.isArray(body.pairs) ? body.pairs : [];

  const pairs =
    rawPairs.length > 0
      ? rawPairs.map((p) => ({
          left: {
            fa: p?.left?.fa || "",
            en: p?.left?.en || "",
          },
          right: {
            fa: p?.right?.fa || "",
            en: p?.right?.en || "",
          },
        }))
      : makeEmptyForm().pairs;

  const layout = body.layout || "left-right";

  const points = item?.points ?? 1;
  const status = item?.status || "published";
  const tags = Array.isArray(item?.tags) ? item.tags.join(",") : "";
  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    pairs,
    layout,
    points,
    status,
    tags,
    explanation_fa,
    explanation_en,
  };
};

export default function MatchingModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) setForm(itemToForm(item));
    else setForm(makeEmptyForm());
  }, [isOpen, isEdit, item]);

  if (!isOpen) return null;

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const addPair = () =>
    setForm((f) => ({
      ...f,
      pairs: [
        ...f.pairs,
        { left: { fa: "", en: "" }, right: { fa: "", en: "" } },
      ],
    }));

  const removePair = (idx) =>
    setForm((f) => {
      if (f.pairs.length <= 1) return f; // حداقل یک جفت
      const nextPairs = f.pairs.filter((_, i) => i !== idx);
      return { ...f, pairs: nextPairs };
    });

  const updatePairSide = (idx, side, lang, val) =>
    setForm((f) => {
      const next = [...f.pairs];
      next[idx] = {
        ...next[idx],
        [side]: { ...next[idx][side], [lang]: val },
      };
      return { ...f, pairs: next };
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.stem_fa.trim() && !form.stem_en.trim()) {
      toast.error(t("matching.toasts.stem_required"));
      return;
    }
    if (form.pairs.length < 2) {
      toast.error(t("matching.toasts.need_two_pairs"));
      return;
    }

    const payload = {
      kind: "matching",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: {
        fa: form.stem_fa || undefined,
        en: form.stem_en || undefined,
      },
      body: {
        pairs: form.pairs.map((p) => ({
          left: { fa: p.left.fa || undefined, en: p.left.en || undefined },
          right: { fa: p.right.fa || undefined, en: p.right.en || undefined },
        })),
        layout: form.layout,
      },
      scoring: { mode: "all_required" },
      points: Number(form.points) || 1,
      status: form.status || "published",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t("matching.toasts.updated", "سؤال تطبیقی با موفقیت ویرایش شد")
          : t("matching.toasts.created", "سؤال تطبیقی با موفقیت ساخته شد")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mb-6 space-y-4">
      {/* Stem */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("matching.stem_fa")}
          </label>
          <CustomEditor
            value={form.stem_fa}
            onEditorChange={(content) => handleEditorChange("stem_fa", content)}
            placeholder={t("matching.stem_fa_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("matching.stem_en")}
          </label>
          <CustomEditor
            value={form.stem_en}
            onEditorChange={(content) => handleEditorChange("stem_en", content)}
            placeholder={t("matching.stem_en_ph")}
          />
        </div>
      </div>

      {/* Layout Selector */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t("matching.layout")}
        </label>
        <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
          {["left-right", "right-left", "bottom-top"].map((layout) => (
            <div key={layout} className="flex items-center">
              <input
                type="radio"
                id={`layout-${layout}`}
                name="layout"
                value={layout}
                checked={form.layout === layout}
                onChange={(e) =>
                  setForm((f) => ({ ...f, layout: e.target.value }))
                }
                className="h-4 w-4 text-purple-600 focus:ring-purple-500"
              />
              <label
                htmlFor={`layout-${layout}`}
                className="ml-2 text-sm text-gray-700"
              >
                {t(`matching.layout_${layout}`)}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Pairs */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium">
            {t("matching.pairs")}
          </label>
          <button
            type="button"
            onClick={addPair}
            className="text-blue-600 text-sm"
          >
            <Plus size={16} className="inline-block mr-1" />
            {t("matching.add_pair")}
          </button>
        </div>

        <div className="space-y-3">
          {form.pairs.map((p, idx) => (
            <div key={idx} className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold">
                  {t("matching.pair_n", { n: idx + 1 })}
                </span>
                {form.pairs.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePair(idx)}
                    className="p-1 rounded-full text-red-600 hover:bg-red-100"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <div className="text-xs font-medium opacity-70">
                    {t("matching.left_col")}
                  </div>
                  <input
                    className="border rounded-lg px-3 py-2"
                    placeholder={t("matching.left_fa_ph")}
                    value={p.left.fa}
                    onChange={(e) =>
                      updatePairSide(idx, "left", "fa", e.target.value)
                    }
                  />
                  <input
                    className="border rounded-lg px-3 py-2"
                    placeholder={t("matching.left_en_ph")}
                    value={p.left.en}
                    onChange={(e) =>
                      updatePairSide(idx, "left", "en", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <div className="text-xs font-medium opacity-70">
                    {t("matching.right_col")}
                  </div>
                  <input
                    className="border rounded-lg px-3 py-2"
                    placeholder={t("matching.right_fa_ph")}
                    value={p.right.fa}
                    onChange={(e) =>
                      updatePairSide(idx, "right", "fa", e.target.value)
                    }
                  />
                  <input
                    className="border rounded-lg px-3 py-2"
                    placeholder={t("matching.right_en_ph")}
                    value={p.right.en}
                    onChange={(e) =>
                      updatePairSide(idx, "right", "en", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("matching.points")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.points}
            onChange={(e) => setForm((f) => ({ ...f, points: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("matching.status")}
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="published">{t("common.published")}</option>
            <option value="draft">{t("common.draft")}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("matching.tags")}
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("common.tags_csv")}
            value={form.tags}
            onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
          />
        </div>
      </div>

      <ExplanationField
        valueFa={form.explanation_fa}
        valueEn={form.explanation_en}
        onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
        onChangeEn={(v) => handleEditorChange("explanation_en", v)}
        isRtl={isRtl}
      />

      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg"
      >
        {isEdit ? t("common.update") : t("common.create")}
      </button>
    </form>
  );
}
